/**
 * Factor Investing (migrada do Streamlit; equivalência em
 * backend/tests_paginas/test_factor.py).
 *
 * O backtest leva ~2 min e roda em segundo plano no servidor (decisão de 2026-10-03): a
 * tela pede, recebe "rodando" e pergunta de novo a cada poucos segundos até "pronto".
 * Configuração na URL (`cfg`). Benchmarks, NAV bruto e a data da posição mudam só a
 * apresentação, sem refazer o backtest (como no legado).
 */
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Loader,
  MultiSelect,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
} from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { ErroApi, apiGet, apiPost, type Envelope } from '../../api/cliente';
import { AvisosApi } from '../../components/AvisosApi';
import { Cronometro } from '../../components/Cronometro';
import { BarraFiltros, CampoData, GrupoFiltros, LARGURA } from '../../components/Filtros';
import { Grafico, type GraficoApi } from '../../components/Grafico';
import { Kpi, type KpiApi } from '../../components/Kpi';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { Tearsheet, type BlocoMovel, type TearsheetApi } from '../../components/Tearsheet';
import { formatarDataHora } from '../../lib/formatos';
import { useFiltrosUrl } from '../../lib/url-state';

const CAMINHO = '/analise-quantitativa/factor-investing';
export const INTERVALO_CONSULTA = 3000;

export interface ConfigFactor {
  inicio: string;
  fim: string;
  estilo: string;
  metricas: string[];
  construcao: string;
  selecao: string;
  quantil: number;
  top_n: number;
  rebalanceamento: string;
  adtv_min: number;
  rf: number;
  custo_bps: number;
  aluguel: number;
  beta_neutro: boolean;
  universo: string[];
  benchmarks: string[];
  bruto: boolean;
  data_rebal: string | null;
}
export const CONFIG_PADRAO: ConfigFactor = {
  inicio: '2019-12-31',
  fim: '',
  estilo: 'Momentum',
  metricas: [],
  construcao: 'long_only',
  selecao: 'quantile',
  quantil: 20,
  top_n: 20,
  rebalanceamento: 'BME',
  adtv_min: 8_000_000,
  rf: 10.75,
  custo_bps: 10,
  aluguel: 2,
  beta_neutro: false,
  universo: [],
  benchmarks: ['IBOV'],
  bruto: false,
  data_rebal: null,
};

export function lerConfig(texto: string): ConfigFactor | null {
  if (!texto) return null;
  try {
    return { ...CONFIG_PADRAO, ...(JSON.parse(texto) as Partial<ConfigFactor>) };
  } catch {
    return null;
  }
}

interface Opcoes {
  estilos: string[];
  construcoes: Record<string, string>;
  selecoes: Record<string, string>;
  rebalanceamentos: Record<string, string>;
  benchmarks: string[];
  universos: Record<string, string>;
  fatores: string[];
}
type Aba = {
  info?: string;
  vazio?: string;
  legenda?: string;
  tabela?: TabelaApi;
  grafico?: GraficoApi;
};
interface Resultado {
  sem_resultado?: string;
  rotulo: string;
  fatores: string[];
  avisos: string[];
  tearsheet: TearsheetApi;
  custos: { kpis: KpiApi[]; legenda: string; custos: Aba; rebals: Aba; eventos: Aba };
  holdings: {
    info?: string;
    posicao?: {
      datas: string[];
      data: string;
      aviso?: string;
      legenda?: string;
      tabela?: TabelaApi;
    };
    historico?: { legenda: string; tabela: TabelaApi };
  };
}
interface Estado {
  status: 'rodando' | 'pronto' | 'erro';
  iniciado_em: string;
  mensagem?: string;
  resultado?: Resultado;
}

const opcoesDe = (r: Record<string, string> | undefined) =>
  Object.entries(r ?? {}).map(([value, label]) => ({ value, label }));

function mensagem(erro: unknown, padrao: string) {
  return erro instanceof ErroApi ? erro.message : padrao;
}

function AbaDiagnostico({ aba }: { aba: Aba }) {
  return (
    <Stack gap="xs" pt="sm">
      {aba.info && <Alert color="blue">{aba.info}</Alert>}
      {aba.grafico && (
        <>
          <Text size="xs" c="dimmed">
            Drag cumulativo como % do NAV (aditivo, não composto).
          </Text>
          <Grafico grafico={aba.grafico} />
        </>
      )}
      {aba.vazio && (
        <Text size="sm" c="dimmed">
          {aba.vazio}
        </Text>
      )}
      {aba.tabela && <TabelaDados tabela={aba.tabela} altura={360} />}
      {aba.legenda && (
        <Text size="xs" c="dimmed">
          {aba.legenda}
        </Text>
      )}
    </Stack>
  );
}

function Formulario({
  inicial,
  opcoes,
  carregando,
  aoRodar,
}: {
  inicial: ConfigFactor;
  opcoes: Opcoes | undefined;
  carregando: boolean;
  aoRodar: (c: ConfigFactor) => void;
}) {
  const [c, setC] = useState(inicial);
  const mudar = (m: Partial<ConfigFactor>) => setC((a) => ({ ...a, ...m }));
  const hoje = new Date().toISOString().slice(0, 10);
  const fim = c.fim || hoje;
  const invalida = fim < c.inicio || (c.estilo === 'Custom' && c.metricas.length === 0);
  const longOnly = c.construcao === 'long_only';
  return (
    <BarraFiltros carregando={carregando}>
      <GrupoFiltros titulo="Período">
        <CampoData
          rotulo="Início"
          valor={c.inicio}
          min="2000-01-01"
          aoMudar={(d) => mudar({ inicio: d })}
        />
        <CampoData
          rotulo="Fim"
          valor={fim}
          min="2000-01-01"
          aoMudar={(d) => mudar({ fim: d === hoje ? '' : d })}
        />
      </GrupoFiltros>
      <GrupoFiltros titulo="Estratégia">
        <Select
          label="Estilo"
          data={opcoes?.estilos ?? []}
          value={c.estilo}
          onChange={(estilo) => estilo && mudar({ estilo })}
          w={160}
        />
        {c.estilo === 'Custom' && (
          <MultiSelect
            label="Métricas"
            description="Fatores da estratégia"
            data={opcoes?.fatores ?? []}
            value={c.metricas}
            onChange={(metricas) => mudar({ metricas })}
            searchable
            w={LARGURA.selecao + 60}
          />
        )}
        <Select
          label="Construção"
          data={opcoesDe(opcoes?.construcoes)}
          value={c.construcao}
          onChange={(construcao) => construcao && mudar({ construcao })}
          w={150}
        />
        <Select
          label="Seleção"
          data={opcoesDe(opcoes?.selecoes)}
          value={c.selecao}
          onChange={(selecao) => selecao && mudar({ selecao })}
          w={130}
        />
        {c.selecao === 'quantile' ? (
          <NumberInput
            label="Quantil por cauda (%)"
            value={c.quantil}
            onChange={(v) => mudar({ quantil: Number(v) || 20 })}
            min={5}
            max={50}
            step={5}
            w={LARGURA.numero + 30}
          />
        ) : (
          <NumberInput
            label="Top N por cauda"
            value={c.top_n}
            onChange={(v) => mudar({ top_n: Number(v) || 20 })}
            min={1}
            max={200}
            w={LARGURA.numero}
          />
        )}
        <Select
          label="Rebalanceamento"
          data={opcoesDe(opcoes?.rebalanceamentos)}
          value={c.rebalanceamento}
          onChange={(rebalanceamento) => rebalanceamento && mudar({ rebalanceamento })}
          w={170}
        />
        <Checkbox
          mt={25}
          label="Beta neutro"
          description={longOnly ? 'Só no Long/Short' : 'Pernas com beta igual'}
          checked={!longOnly && c.beta_neutro}
          disabled={longOnly}
          onChange={(e) => mudar({ beta_neutro: e.currentTarget.checked })}
        />
      </GrupoFiltros>
      <GrupoFiltros titulo="Universo e custos">
        <MultiSelect
          label="Índices B3"
          description="Membros na data de cada rebalanceamento"
          placeholder={c.universo.length ? undefined : 'Todas as ações BRL'}
          data={opcoesDe(opcoes?.universos)}
          value={c.universo}
          onChange={(universo) => mudar({ universo })}
          clearable
          w={LARGURA.selecao}
        />
        <NumberInput
          label="ADTV mínimo (21d)"
          description={
            c.universo.length && c.adtv_min > 0 ? 'Soma com o índice; 0 = só o índice' : undefined
          }
          value={c.adtv_min}
          onChange={(v) => mudar({ adtv_min: Number(v) || 0 })}
          min={0}
          step={500_000}
          thousandSeparator="."
          decimalSeparator=","
          w={LARGURA.numero + 40}
        />
        <NumberInput
          label="Taxa livre de risco (% a.a.)"
          value={c.rf}
          onChange={(v) => mudar({ rf: Number(v) || 0 })}
          min={0}
          max={50}
          step={0.25}
          decimalScale={2}
          decimalSeparator=","
          w={LARGURA.numero + 60}
        />
        <NumberInput
          label="Custo de trading (bps)"
          description="Por real negociado"
          value={c.custo_bps}
          onChange={(v) => mudar({ custo_bps: Number(v) || 0 })}
          min={0}
          decimalScale={1}
          decimalSeparator=","
          w={LARGURA.numero + 30}
        />
        <NumberInput
          label="Aluguel de short (% a.a.)"
          description={longOnly ? 'Só no Long/Short' : 'Sobre o notional short'}
          value={longOnly ? 0 : c.aluguel}
          disabled={longOnly}
          onChange={(v) => mudar({ aluguel: Number(v) || 0 })}
          min={0}
          max={50}
          step={0.25}
          decimalScale={2}
          decimalSeparator=","
          w={LARGURA.numero + 40}
        />
      </GrupoFiltros>
      <GrupoFiltros titulo="Rodar">
        <Button mt={25} color="dourado" disabled={invalida} onClick={() => aoRodar(c)}>
          Rodar backtest
        </Button>
      </GrupoFiltros>
    </BarraFiltros>
  );
}

export function FactorInvesting() {
  const [filtros, definir] = useFiltrosUrl({ cfg: '' });
  const config = lerConfig(filtros.cfg);
  const corpo = config ? { ...config, fim: config.fim || null } : null;
  const mudarVista = (m: Partial<ConfigFactor>) =>
    config && definir({ cfg: JSON.stringify({ ...config, ...m }) });

  const opcoes = useQuery({
    queryKey: ['quant.factor.opcoes'],
    queryFn: ({ signal }) => apiGet<Envelope<Opcoes>>(`${CAMINHO}/opcoes`, {}, signal),
    staleTime: 60 * 60 * 1000,
  });
  const consulta = useQuery({
    queryKey: ['quant.factor', corpo],
    queryFn: ({ signal }) => apiPost<Envelope<Estado>>(CAMINHO, corpo, signal),
    enabled: !!config,
    placeholderData: keepPreviousData,
    // Continua consultando com a aba em segundo plano: quem troca de aba durante os ~2 min
    // encontra o resultado pronto ao voltar.
    refetchIntervalInBackground: true,
    refetchInterval: (q) => (q.state.data?.data.status === 'rodando' ? INTERVALO_CONSULTA : false),
  });
  const estado = consulta.data?.data;
  const r = estado?.status === 'pronto' ? estado.resultado : undefined;
  const buscarMovel = async (janela: string, sinal: AbortSignal) =>
    (await apiPost<Envelope<BlocoMovel>>(`${CAMINHO}/movel`, { cfg: corpo, janela }, sinal)).data;

  // A configuração pesada (o que refaz o backtest) define o formulário; a vista não.
  const chaveFormulario = config
    ? JSON.stringify({ ...config, benchmarks: [], bruto: false, data_rebal: null })
    : '';

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Factor Investing"
        descricao="Backtest de estratégias fatoriais em ações brasileiras: estilo ou fatores escolhidos, universo por liquidez (ADTV) e/ou por índices B3, construção long only ou long/short (opcionalmente beta neutro), custos de trading e aluguel."
        dadosAte={estado?.status === 'pronto' ? consulta.data?.meta.dados_ate : undefined}
        fonte="Banco Persevera (factor_zoo, composição de índices B3, indicadores) · Fibery (fatores) · persevera_tools"
      />
      <Formulario
        key={chaveFormulario}
        inicial={config ?? CONFIG_PADRAO}
        opcoes={opcoes.data?.data}
        carregando={opcoes.isFetching || (consulta.isFetching && estado?.status !== 'rodando')}
        aoRodar={(c) => definir({ cfg: JSON.stringify({ ...c, data_rebal: null }) })}
      />
      <AvisosApi avisos={[opcoes.data?.meta.avisos]} />
      {!config && (
        <Alert color="blue">
          Configure a estratégia e clique em <b>Rodar backtest</b>. O cálculo leva cerca de 2
          minutos e roda no servidor: pode deixar a página aberta ou voltar depois pelo mesmo link.
        </Alert>
      )}
      {consulta.isError && (
        <Alert color="red">{mensagem(consulta.error, 'Não foi possível rodar o backtest.')}</Alert>
      )}
      {config && consulta.isPending && <Loader />}
      {estado?.status === 'rodando' && (
        <Alert color="blue" icon={<Loader size="sm" />} title="Rodando o backtest">
          Iniciado em {formatarDataHora(estado.iniciado_em)} (há{' '}
          <Cronometro desde={estado.iniciado_em} />
          ). Leva cerca de 2 minutos; o resultado aparece aqui sozinho.
        </Alert>
      )}
      {estado?.status === 'erro' && (
        <Alert color="red" title="O backtest falhou">
          {estado.mensagem} Tente de novo em 1 minuto.
        </Alert>
      )}
      {r?.sem_resultado && <Alert color="yellow">{r.sem_resultado}</Alert>}
      {r && !r.sem_resultado && (
        <>
          <Secao
            titulo="Resultado"
            legenda={
              <>
                {r.rotulo}
                {r.fatores.length > 0 && <> · Fatores: {r.fatores.join(', ')}</>}
              </>
            }
            acoes={
              <Group gap="md">
                <MultiSelect
                  aria-label="Benchmarks"
                  placeholder={config?.benchmarks.length ? '' : 'Sem benchmark'}
                  data={opcoes.data?.data.benchmarks ?? []}
                  value={config?.benchmarks ?? []}
                  onChange={(benchmarks) => mudarVista({ benchmarks })}
                  size="xs"
                  w={200}
                />
                <Checkbox
                  label="Mostrar NAV bruto"
                  checked={!!config?.bruto}
                  onChange={(e) => mudarVista({ bruto: e.currentTarget.checked })}
                />
              </Group>
            }
          >
            <AvisosApi avisos={[r.avisos]} />
          </Secao>
          <Tearsheet
            key={JSON.stringify([config?.benchmarks, config?.bruto, consulta.dataUpdatedAt])}
            dados={r.tearsheet}
            chave={corpo}
            buscarMovel={buscarMovel}
          />
          <Secao titulo="Custos e qualidade" legenda={r.custos.legenda}>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
              {r.custos.kpis.map((k) => (
                <Kpi key={k.rotulo} kpi={k} />
              ))}
            </SimpleGrid>
            <Tabs defaultValue="custos" keepMounted={false} color="dourado" mt="md">
              <Tabs.List>
                <Tabs.Tab value="custos">Custos</Tabs.Tab>
                <Tabs.Tab value="rebals">Rebalances</Tabs.Tab>
                <Tabs.Tab value="eventos">Eventos</Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="custos">
                <AbaDiagnostico aba={r.custos.custos} />
              </Tabs.Panel>
              <Tabs.Panel value="rebals">
                <AbaDiagnostico aba={r.custos.rebals} />
              </Tabs.Panel>
              <Tabs.Panel value="eventos">
                <AbaDiagnostico aba={r.custos.eventos} />
              </Tabs.Panel>
            </Tabs>
          </Secao>
          <Secao titulo="Holdings" legenda="Carteira em cada rebalanceamento: pesos (%) e score.">
            {r.holdings.info && <Alert color="blue">{r.holdings.info}</Alert>}
            {r.holdings.posicao && r.holdings.historico && (
              <Tabs defaultValue="posicao" keepMounted={false} color="dourado">
                <Tabs.List>
                  <Tabs.Tab value="posicao">Posição</Tabs.Tab>
                  <Tabs.Tab value="historico">Histórico</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="posicao">
                  <Stack gap="xs" pt="sm">
                    <Select
                      label="Data de rebalanceamento"
                      data={[...r.holdings.posicao.datas].reverse()}
                      value={r.holdings.posicao.data}
                      onChange={(d) => d && mudarVista({ data_rebal: d })}
                      w={LARGURA.selecao}
                    />
                    {r.holdings.posicao.aviso && (
                      <Alert color="yellow">{r.holdings.posicao.aviso}</Alert>
                    )}
                    {r.holdings.posicao.legenda && (
                      <Text size="xs" c="dimmed">
                        {r.holdings.posicao.legenda}
                      </Text>
                    )}
                    {r.holdings.posicao.tabela && (
                      <TabelaDados tabela={r.holdings.posicao.tabela} altura={400} />
                    )}
                  </Stack>
                </Tabs.Panel>
                <Tabs.Panel value="historico">
                  <Stack gap="xs" pt="sm">
                    <Text size="xs" c="dimmed">
                      {r.holdings.historico.legenda}
                    </Text>
                    <TabelaDados tabela={r.holdings.historico.tabela} altura={400} />
                  </Stack>
                </Tabs.Panel>
              </Tabs>
            )}
          </Secao>
        </>
      )}
    </Pagina>
  );
}
